import React from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Label,
  Input,
  Form,
  FormGroup,
  Button
} from "reactstrap";

const ExperienceForm = ({
  experience,
  experiences,
  errors,
  pendingError,
  handleExperienceChange,
  addExperience,
  removeExperience,
  clearPending,
  t
}) => {
  const hasInput = Boolean(
    experience.company || experience.position || experience.startDate || experience.endDate || experience.activities || experience.isCurrentJob
  );
  return (
    <Card className="mb-4" id="experience">
      <CardBody>
        <CardTitle className="h4">{t("Experience")}</CardTitle>
        <p className="card-title-desc">
          {t("Fill in one experience and tap \"Add\" to include it in the list. Repeat for each job. Then save the curriculum.")}
        </p>
        {pendingError && (
          <div className="alert alert-warning py-2" role="alert">
            <i className="bx bx-error-circle me-1" />{pendingError}
          </div>
        )}

        <Form onSubmit={(e) => { e.preventDefault(); addExperience(); }}>
          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="company">{t("Company Name")}</Label>
                <Input
                  type="text"
                  id="company"
                  name="company"
                  value={experience.company}
                  onChange={handleExperienceChange}
                  placeholder={t("Enter company name")}
                  invalid={!!errors.company}
                />
                {errors.company && (
                  <div className="text-danger small mt-1">
                    {errors.company}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="position">{t("Position")}</Label>
                <Input
                  type="text"
                  id="position"
                  name="position"
                  value={experience.position}
                  onChange={handleExperienceChange}
                  placeholder={t("Enter position")}
                  invalid={!!errors.position}
                />
                {errors.position && (
                  <div className="text-danger small mt-1">
                    {errors.position}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="startDate">{t("Start Date")}</Label>
                <Input
                  type="text"
                  id="startDate"
                  name="startDate"
                  value={experience.startDate}
                  onChange={handleExperienceChange}
                  placeholder="DD/MM/AAAA"
                  inputMode="numeric"
                  maxLength={10}
                  invalid={!!errors.startDate}
                />
                {errors.startDate && (
                  <div className="text-danger small mt-1">
                    {errors.startDate}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="endDate">{t("End Date")}</Label>
                <Input
                  type="text"
                  id="endDate"
                  name="endDate"
                  value={experience.endDate}
                  onChange={handleExperienceChange}
                  placeholder="DD/MM/AAAA"
                  inputMode="numeric"
                  disabled={experience.isCurrentJob}
                  maxLength={10}
                  invalid={!!errors.endDate}
                />
                {errors.endDate && (
                  <div className="text-danger small mt-1">
                    {errors.endDate}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>
          
          <FormGroup className="mb-3">
            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="isCurrentJob"
                name="isCurrentJob"
                checked={experience.isCurrentJob}
                onChange={handleExperienceChange}
              />
              <Label className="form-check-label" for="isCurrentJob">
                {t("Current Job")}
              </Label>
            </div>
          </FormGroup>
          
          <FormGroup className="mb-3">
            <Label for="activities">{t("Activities")}</Label>
            <Input
              type="textarea"
              id="activities"
              name="activities"
              value={experience.activities}
              onChange={handleExperienceChange}
              placeholder={t("Enter activities")}
              rows={3}
            />
          </FormGroup>
          
          <div className="d-flex flex-wrap gap-2 mb-4">
            <Button color="primary" outline type="submit">
              <i className="bx bx-plus me-1" />{t("Add Experience")}
            </Button>
            {hasInput && (
              <Button color="link" className="text-muted" type="button" onClick={clearPending}>
                {t("Clear")}
              </Button>
            )}
          </div>
          
          {/* Added experiences — card rows work on any screen width */}
          {experiences.length > 0 ? (
            <div aria-live="polite">
              <h6 className="text-muted mb-2">{t("Added experiences")} ({experiences.length})</h6>
              {experiences.map(exp => (
                <div key={exp.id} className="list-card p-3 d-flex justify-content-between align-items-start gap-3">
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{exp.position}</div>
                    <div className="text-muted">{exp.company}</div>
                    <div className="text-muted small">
                      {exp.startDate} – {exp.isCurrentJob ? t("Current Job") : (exp.endDate || "—")}
                    </div>
                    {exp.activities && <div className="small mt-1">{exp.activities}</div>}
                  </div>
                  <Button color="danger" outline size="sm" className="btn-icon-tap" onClick={() => removeExperience(exp.id)}
                    aria-label={`${t("Remove")} ${exp.position}`} title={t("Remove")}>
                    <i className="bx bx-trash" aria-hidden="true" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted">
              {t("No experiences added yet")}
            </div>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

ExperienceForm.propTypes = {
  experience: PropTypes.object.isRequired,
  experiences: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  pendingError: PropTypes.string,
  handleExperienceChange: PropTypes.func.isRequired,
  addExperience: PropTypes.func.isRequired,
  removeExperience: PropTypes.func.isRequired,
  clearPending: PropTypes.func,
  t: PropTypes.func.isRequired
};

export default ExperienceForm;